import { useState, useEffect, useContext } from "react";
import FilterSelect from "../Filter/FilterSelect";
import MonthRangeDropdown from "../Filter/MonthRangeDropdown";
import MultiLineChart from "../d3Charts/MultiLineChart/MultiLineChart";
import "./Timeseries.scss";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { data } from "react-router-dom";
import TimeFilter from "../Filter/TimeFilter/TimeFilter";
import AxiosService from "../../utils/AxiosService";
import { AppContext } from "../../context/AppContext";
import { KEY_NAME } from "../../utils/KeyName";
import Loader from "../Loader/Loader";
import { SEARCH_PARAMS } from "../../utils/SearchParams";
import { useCustomSearchParams } from "../hooks/useCustomSearchParams";
import DateRangeBadge from "../DateRangeBadge/DateRangeBadge";
import { colorArray } from "../../utils/theme";
import { timer } from "d3";
import DataNotFound from "../DataNotFound/DataNotFound";
import SomethingWentWrong from "../SomethingWentWrong/SomethingWentWrong";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";

const TimeSeriesInner = ({ queryDataObj }) => {
  const { get, set, setMultiple } = useCustomSearchParams();
  const [shortName, setShortName] = useState(get(SEARCH_PARAMS.SHORT_NAME));
  const [data, setData] = useState(null);
  const [timeRange, setTimeRange] = useState("all");
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [monthRange, setMonthRange] = useState({
    // from: { year: 2023, month: 0 },
    // to: { year: 2024, month: 0 },
  });
  const [showDateRangeBadge, setShowDateRangeBadge] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filteredData, setFilteredData] = useState(null);

  const axiosService = new AxiosService();
  const { entityName, selectedFilterBy } = useContext(AppContext);

  const timeOptions = [
    { label: "All time data", value: "all" },
    { label: "Custom month range", value: "custom" },
  ];

  const handleFilterData = () => {
    const fromDate = new Date(monthRange.from.year, monthRange.from.month, 1);
    const nextMonth = new Date(monthRange.to.year, monthRange.to.month + 1, 1);

    const filteredData = data.data[0].values
      .map((item, originalIndex) => ({
        ...item,
        index: originalIndex,
      }))
      .filter((item) => {
        const d = new Date(item.date);
        return d >= fromDate && d < nextMonth;
      });

    const startIndex = filteredData?.[0]?.index;
    const arrayLength = filteredData?.length;

    setFilteredData({
      config: data.config,
      data: data.data.map((items, index) => {
        return {
          label: items.label,
          values: items.values.slice(startIndex, startIndex + arrayLength),
        };
      }),
    });
  };

  useEffect(() => {
    if (data) {
      handleFilterData();
    }
  }, [monthRange, timeRange]);

  /* 🔑 open calendar ONLY when custom is selected */
  useEffect(() => {
    if (timeRange?.value === "custom") {
      setIsMonthPickerOpen(true);
    } else {
      setIsMonthPickerOpen(false);
      setMonthRange({ from: startDate, to: endDate });
    }

    if (timeRange?.value === "all") {
      setShowDateRangeBadge(false);
    }
  }, [timeRange]);

  useEffect(() => {
    if (data) {
      const startDate = new Date(data.data[0].values[0].date);
      const endDate = new Date(data.data[0].values.slice(-1)[0].date);
      setFilteredData(data);
      setStartDate({
        year: startDate.getFullYear(),
        month: startDate.getMonth(),
      });
      setEndDate({ year: endDate.getFullYear(), month: endDate.getMonth() });
      setMonthRange(() => ({
        from: { year: startDate.getFullYear(), month: startDate.getMonth() },
        to: { year: endDate.getFullYear(), month: endDate.getMonth() },
      }));
    }
  }, [data]);

  useEffect(() => {
    // hasFetched.current = true;
    if (!entityName) return;
    setIsLoading(true);
    setHasError(false);
    switch (entityName) {
      case KEY_NAME.FUNDER:
        axiosService
          .getFundersTimeSeries({ ...queryDataObj }, shortName)
          .then((response) => {
            setData(response.data);
          })
          .catch((error) => {
            console.error("Error:", error);
            setData(null);
            setHasError(true);
          })
          .finally(() => {
            setIsLoading(false);
          });

        break;
      case KEY_NAME.PUBLISHER:
      case KEY_NAME.INSTITUTION:
        axiosService
          .getPublisherTimeSeries({ ...queryDataObj }, shortName)
          .then((response) => {
            setData(response.data);
          })
          .catch((error) => {
            console.error("Error:", error);
            setData(null);
            setHasError(true);
          })
          .finally(() => {
            setIsLoading(false);
          });
        break;
      // case KEY_NAME.INSTITUTION:
      //   axiosService
      //     .getPublisherTimeSeries({ ...queryDataObj }, shortName)
      //     .then((response) => {
      //       setData(response.data);
      //     })
      //     .catch((error) => {
      //       console.error("Error:", error);
      //     })
      //     .finally(() => {
      //       setIsLoading(false);
      //     });
      //   break;

      default:
        break;
    }
  }, [entityName, queryDataObj]);

  useEffect(() => {
    setIsLoading(true);
    if (data) {
      setIsLoading(false);
    }
  }, [data]);

  // useEffect(() => {
  //   if (queryDataObj?.publisherFilter) {
  //     console.log("queryDataObj =>", queryDataObj);
  //   }
  // }, [queryDataObj?.publisherFilter]);

  return (
    <div className="time-series position-relative">
      <div
        className={`time-series__topbar ${showDateRangeBadge ? "time-series__with_badge" : ""} d-flex gap-2`}
        style={{ zIndex: 100 }}
      >
        {/* <div className="time-series__filters"> */}
        <div className="time-series-custom_range_titer">
          {selectedFilterBy === "ALL" && filteredData && (
            <TimeFilter
              label=""
              width="full"
              startDate={startDate}
              filterValue={timeRange}
              options={timeOptions}
              selectedValue={timeRange}
              onChange={setTimeRange}
              open={isMonthPickerOpen}
              monthRangeValue={monthRange}
              onApply={(range) => {
                setMonthRange(range);
                setIsMonthPickerOpen(false);
                setShowDateRangeBadge(true);
              }}
              onCancel={() => {
                // If no custom range was ever applied (no badge showing),
                // reset the dropdown back to "All time data"
                if (!showDateRangeBadge) {
                  setTimeRange("all");
                }
              }}
            />
          )}
        </div>

        {showDateRangeBadge && (
          <DateRangeBadge
            range={monthRange}
            setTimeRange={setTimeRange}
            setShowDateRangeBadge={setShowDateRangeBadge}
            timeOptions={timeOptions}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : hasError ? (
        <SomethingWentWrong />
      ) : filteredData ? (
        <div className="time-series-chart">
          <MultiLineChart
            data={filteredData}
            title={`${shortName} Time Series Tracked by CHORUS`}
            colorArray={colorArray}
          />
        </div>
      ) : (
        <DataNotFound />
      )}
    </div>
  );
};

const TimeSeries = (props) => (
  <ErrorBoundary>
    <TimeSeriesInner {...props} />
  </ErrorBoundary>
);

export default TimeSeries;
